/**
 * Project Form Modal
 *
 * Modal for creating or editing projects with multiple fields:
 * - Name (required)
 * - Description (optional)
 * - Agent Prompt (optional) - Initial user prompt that starts the project
 */

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/Button';

interface ProjectFormData {
  name: string;
  description?: string;
  agentPrompt?: string;
}

interface ProjectFormModalProps {
  isOpen: boolean;
  title: string;
  initialData?: Partial<ProjectFormData>;
  onSave: (data: ProjectFormData) => void;
  onCancel: () => void;
}

export const ProjectFormModal: React.FC<ProjectFormModalProps> = ({
  isOpen,
  title,
  initialData,
  onSave,
  onCancel
}) => {
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [agentPrompt, setAgentPrompt] = useState(initialData?.agentPrompt || '');

  // Reset values when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name || '');
      setDescription(initialData?.description || '');
      setAgentPrompt(initialData?.agentPrompt || '');
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        agentPrompt: agentPrompt.trim() || undefined
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div
        className="bg-background border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 space-y-4 overflow-y-auto">
            {/* Project Name */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Project Name <span className="text-orange-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="e.g., Password Reset Skill"
                autoFocus
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Description <span className="text-muted-foreground text-xs">(optional)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 min-h-[80px] resize-y"
                placeholder="Brief description of what this project does..."
                rows={3}
              />
            </div>

            {/* Agent Prompt */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Agent Prompt <span className="text-muted-foreground text-xs">(optional)</span>
              </label>
              <p className="text-xs text-muted-foreground mb-2">
                The initial prompt that describes what you want to build. This helps the AI assistant understand your project goals.
              </p>
              <textarea
                value={agentPrompt}
                onChange={(e) => setAgentPrompt(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 min-h-[120px] resize-y"
                placeholder="e.g., Create a skill that helps users reset their passwords by verifying their identity and generating a secure reset link..."
                rows={5}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit">
              {initialData?.name ? 'Save' : 'Create Project'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
